import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck, Ticket, FileText, Server, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export function Notifications() {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingAll, setMarkingAll] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const res = await notificationsAPI.getAll();
            setNotifications(res.data);
        } catch (error) {
            console.error('Load error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (id) => {
        try {
            await notificationsAPI.markRead(id);
            setNotifications(notifications.map(n => 
                n.id === id ? { ...n, is_read: true } : n
            ));
        } catch (err) {
            toast.error('İşlem başarısız oldu');
        }
    };

    const handleMarkAllRead = async () => {
        setMarkingAll(true);
        try {
            await notificationsAPI.markAllRead();
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
            toast.success('Tüm bildirimler okundu olarak işaretlendi');
        } catch (err) {
            toast.error('İşlem başarısız oldu');
        } finally {
            setMarkingAll(false);
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'ticket':
                return <Ticket className="w-5 h-5" />;
            case 'proposal':
                return <FileText className="w-5 h-5" />;
            case 'asset':
                return <Server className="w-5 h-5" />;
            default:
                return <Bell className="w-5 h-5" />;
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    if (loading) {
        return (
            <div className="space-y-4 max-w-2xl mx-auto">
                <Skeleton className="h-12 w-full" />
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20" />)}
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="notifications-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Bildirimler</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        {unreadCount > 0 ? `${unreadCount} okunmamış bildirim` : 'Tüm bildirimler okundu'}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <Button
                        variant="outline"
                        onClick={handleMarkAllRead}
                        disabled={markingAll}
                        data-testid="mark-all-read-btn"
                    >
                        {markingAll ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <CheckCheck className="w-4 h-4 mr-2" />
                        )}
                        Tümünü Okundu İşaretle
                    </Button>
                )}
            </div>

            {/* Notifications List */}
            <div className="space-y-3">
                {notifications.length > 0 ? (
                    notifications.map((notification) => (
                        <Card
                            key={notification.id}
                            className={`transition-all ${!notification.is_read ? 'border-l-4 border-l-orwion-green bg-orwion-green/5' : ''}`}
                            data-testid={`notification-${notification.id}`}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-start gap-4">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        !notification.is_read 
                                            ? 'bg-orwion-green text-white' 
                                            : 'bg-slate-100 text-slate-500'
                                    }`}>
                                        {getIcon(notification.notification_type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <h3 className={`font-medium ${!notification.is_read ? 'text-slate-800' : 'text-slate-600'}`}>
                                                    {notification.title}
                                                </h3>
                                                <p className="text-sm text-slate-500 mt-0.5">{notification.message}</p>
                                            </div>
                                            {!notification.is_read && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleMarkRead(notification.id)}
                                                    className="flex-shrink-0"
                                                    data-testid={`mark-read-${notification.id}`}
                                                >
                                                    <Check className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 mt-2">
                                            <span className="text-xs text-slate-400">
                                                {formatDateTime(notification.created_at)}
                                            </span>
                                            {notification.link && (
                                                <Link
                                                    to={notification.link}
                                                    className="text-xs text-orwion-green hover:underline"
                                                    data-testid={`notification-link-${notification.id}`}
                                                >
                                                    Detayları Gör
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    <Card>
                        <CardContent className="p-12 text-center">
                            <Bell className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                            <h3 className="font-semibold text-slate-600 mb-1">Bildirim Yok</h3>
                            <p className="text-sm text-slate-400">Henüz bildiriminiz bulunmuyor.</p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}

export default Notifications;
